<template>
  <div :class="containerClasses">
    <h2 v-if="title" class="section-title">{{ title }}</h2>
    <div class="news-grid">
      <router-link
        :to="`/noticia/${article.id}`"
        v-for="article in displayedNews" 
        :key="article.id"
        class="news-card screen-mount"
      >
        <div class="news-image" :style="{ backgroundImage: `url(${article.image})` }">
          <span class="news-category">{{ article.category }}</span>
        </div>
        <div class="news-content">
          <h3 class="news-title">{{ article.title }}</h3>
          <p class="news-excerpt">{{ article.excerpt }}</p>
          <div class="news-footer">
            <span class="news-date">{{ article.date }}</span>
            <button class="read-more">Ler mais →</button>
          </div>
        </div>
      </router-link>
    </div>
    <div v-if="hasMore" class="load-more-container">
      <button @click="loadMore" class="btn-load-more">
        Ver mais {{ category || 'notícias' }} →
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NewsGrid',
  props: {
    news: {
      type: Array,
      required: true
    },
    category: {
      type: String,
      default: null
    },
    title: {
      type: String,
      default: 'Últimas Notícias'
    },
    itemsPerPage: {
      type: Number,
      default: 12
    }
  },
  data() {
    return {
      currentPage: 1,
      screenObserver: null
    }
  },
  computed: {
    filteredNews() {
      if (!this.category) {
        return this.news;
      }
      return this.news.filter(item => item.category === this.category);
    },
    containerClasses() {
      return [
        'news-grid-container',
        { 'news-grid-container--all': this.title === 'Todas as Notícias' }
      ];
    },
    displayedNews() {
      return this.filteredNews.slice(0, this.currentPage * this.itemsPerPage).map(article => ({
        ...article,
        date: article.date.split(' às')[0]
      }));
    },
    hasMore() {
      return this.filteredNews.length > this.displayedNews.length;
    }
  },
  methods: {
    loadMore() {
      this.currentPage++;
      this.$nextTick(() => this.observeScreenMounts());
    },
    setupScreenObserver() {
      if (this.screenObserver) {
        this.screenObserver.disconnect();
      }
      this.screenObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('screen-mount--visible');
            this.screenObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.35,
        rootMargin: '0px 0px -10% 0px'
      });
      this.$nextTick(() => this.observeScreenMounts());
    },
    observeScreenMounts() {
      if (!this.screenObserver || !this.$el) return;
      const cards = this.$el.querySelectorAll('.screen-mount');
      cards.forEach(card => {
        if (!card.classList.contains('screen-mount--visible')) {
          this.screenObserver.observe(card);
        }
      });
    }
  },
  mounted() {
    this.setupScreenObserver();
  },
  beforeUnmount() {
    if (this.screenObserver) {
      this.screenObserver.disconnect();
    }
  },
  watch: {
    category() {
      this.currentPage = 1;
      this.$nextTick(() => this.observeScreenMounts());
    },
    news() {
      this.currentPage = 1;
      this.$nextTick(() => this.observeScreenMounts());
    }
  }
}
</script>

<style scoped>
@import '@/assets/animations/screenMount.css';
.news-grid-container {
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 1rem;
}

.news-grid-container--all {
  margin-top: 6.5rem;
}

.section-title {
  font-size: 40px;
  margin-bottom: 2.5rem;
  color: var(--brand-cloud);
  text-align: center;
  font-weight: 800;
  letter-spacing: -1px;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

@media (max-width: 1024px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .news-grid {
    grid-template-columns: 1fr;
  }
}

.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 3rem;
}

.btn-load-more {
  padding: 1rem 2.5rem;
  background: var(--brand-navy-soft);
  color: #171d3a;
  border: none;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 22px 35px rgba(0, 0, 0, 0.25);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-load-more:hover {
  transform: translateY(-2px);
  box-shadow: 0 26px 42px rgba(0, 0, 0, 0.35);
  background: #b6c4ff;
}


.news-card {
  background: #e5e9fd;
  color: #171d3a;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid rgba(23, 29, 58, 0.08);
  text-decoration: none;
  display: flex;
  flex-direction: column;
}

.news-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 35px 70px rgba(0, 0, 0, 0.28);
  border-color: rgba(23, 29, 58, 0.18);
}

.news-image {
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;
}

.news-category {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: var(--brand-navy);
  color: #ffffff;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.news-content {
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.news-title {
  font-size: 21px;
  margin: 0 0 1rem 0;
  color: #171d3a;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.news-excerpt {
  color: rgba(23, 29, 58, 0.75);
  line-height: 1.6;
  margin-bottom: 1rem;
  flex-grow: 1;
}

.news-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
}

.news-date {
  font-size: 14px;
  color: #64748b;
}

.read-more {
  background: var(--brand-navy);
  border: none;
  color: #f7fbff;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 10px 20px rgba(0, 35, 73, 0.18);
}

.read-more:hover {
  transform: translateX(5px);
  box-shadow: 0 14px 26px rgba(0, 35, 73, 0.28);
  background: #0e2b5a;
}

@media (max-width: 768px) {
  .news-grid {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 32px;
  }
  
  .news-grid-container {
    margin: 2rem auto;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 29px;
    margin-bottom: 1.5rem;
  }
  
  .news-grid-container {
    padding: 0 0.5rem;
    margin: 2rem auto;
  }
  
  .news-grid {
    gap: 1.5rem;
  }
  
  .news-image {
    height: 180px;
  }
  
  .news-content {
    padding: 1rem;
  }
  
  .news-title {
    font-size: 18px;
  }
  
  .news-excerpt {
    font-size: 14px;
  }
  
  .news-footer {
    flex-direction: column;
    gap: 0.8rem;
    align-items: flex-start;
  }
  
  .read-more {
    width: 100%;
    text-align: center;
  }
  
  .btn-load-more {
    padding: 0.8rem 2rem;
    font-size: 14px;
  }

}
</style>
